<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-stepper :steps="rmSteps" :current="currentStep" />
        <hm-spinner v-if="!ready" />
        <Loading :active="busy" :is-full-page="false">
          <slot>
            <hm-spinner />
          </slot>
        </Loading>
        <hm-alert />

        <b-form @submit.prevent="onSubmit" v-show="ready">
          <h4>Step 2: Manage Your Threat-Vulnerability List</h4>
          <hr />
          <p>
            On this page you are being asked to identify all threat-vulnerability combinations unique to your practice circumstances.
            <u>Focus your list to only those you reasonably anticipate</u>.
          </p>

          <h6>Threat</h6>
          <p>&quot;The potential for a person or thing to exercise (accidentally trigger or intentionally exploit) a specific vulnerability.&quot;</p>

          <h6>Vulnerability</h6>
          <p>&quot;A flaw or weakness in system security procedures, design, implementation, or internal controls that could be exercised (accidentally triggered or intentionally exploited) and result in a security breach or a violation of the system’s security policy.&quot;</p>
          <hm-note>
            <u>Important</u>: This list of threat-vulnerability combinations is not all-inclusive. If you require additional threats or vulnerabilities to meet your unique practice environment please let us know.
            <b-button variant="secondary" size="sm" :disabled="busy" @click="dlgRequest = true">Make Request</b-button>
          </hm-note>

          <div class="py-3" v-for="(threatGroup, threatGroupIdx) in currentThreats" :key="'group-' + threatGroup.type">
            <div v-if="threatGroup.type == 'audit'">
              <strong>The practice should <u>reasonably anticipate</u> the following <u>{{threatGroup.type}}</u> threats.</strong>
              This threat-vulnerability combination is a selected by default and cannot be changed.
            </div>
            <div v-else>
              <strong>Does the practice <u>reasonably anticipate</u> any of the following <u>{{threatGroup.type}}</u> threats?</strong>
              If yes, identify associated vulnerabilities specific to the practice.
            </div>

            <div role="tablist" class="pt-4">
              <b-card no-body class="mb-1" v-for="(threat, threatIdx) in threatGroup.threats" :key="'threat-' + threatGroup.type + '-' + threat.threat">
                <b-card-header header-tag="header" class="p-2" role="tab" v-b-toggle="'accordian-' + threatGroupIdx + '-' + threatIdx">
                  {{threat.threat}}
                  <hm-popover v-if="threat.vulns[0].threatInfo" :title="threat.threat" :content="threat.vulns[0].threatInfo" />
                  <div class="when-opened float-right px-2">
                    Vulnerabilities
                    <i class="icon-arrow-down small pl-2"></i>
                  </div>
                  <b-badge class="when-closed float-right px-2" :variant="threat.count > 0 ? 'danger' : 'primary'">{{threat.count}}</b-badge>
                </b-card-header>

                <b-collapse :id="'accordian-' + threatGroupIdx + '-' + threatIdx" visible accordion="threats-accordion" role="tabpanel">
                  <b-card-body>
                    <div v-for="vuln in threat.vulns" :key="vuln.id">
                      <hm-switch class="mx-1 pt-2" size="sm" data-type="check" :disabled="busy || vuln.required" v-model="vuln.active" @change="toggle(threatGroup.type, threat, vuln)">
                        {{vuln.vuln}}
                        <hm-popover v-if="vuln.vulnInfo" :title="vuln.vuln" :content="vuln.vulnInfo" />
                      </hm-switch>
                    </div>
                  </b-card-body>
                </b-collapse>
              </b-card>
            </div>
          </div>

          <hr />
          <b-row>
            <b-col cols="6">
              <b-button type="button" variant="secondary" class="px-4" :disabled="busy" to="/risk/review">Back</b-button>
            </b-col>
            <b-col cols="6" class="text-right">
              <b-button type="submit" variant="primary" class="px-4" :disabled="busy || activeThreats == 0">Next</b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-col>
    </b-row>

    <!-- Request Modal Dialog -->
    <b-modal id="dlgRequest" class="modal-primary" hide-header-close v-model="dlgRequest">
      <div class="font-weight-bold" slot="modal-title">
        <i class="icon-bubble pr-2"></i>Threat or Vulnerability Request
      </div>
      <hm-alert />
      <p>What threat or vulnerability would you like us to add?</p>
      <b-input-group class="mb-3">
        <b-form-textarea
          no-resize
          :rows="3"
          name="dlgRequestMsg"
          class="form-control"
          placeholder="What are you looking for?"
          v-model="dlgRequestMsg"
          v-validate="'required'"
          :disabled="busy"
          :class="{ 'is-invalid': errors.has('dlgRequestMsg') && dlgRequestSubmitted }"
        />
        <b-form-invalid-feedback id="dlgRequestMsgFeedback">Please tell us what threat or vulnerability you would like to see added</b-form-invalid-feedback>
      </b-input-group>
      <div slot="modal-footer">
        <b-button variant="default" :disabled="busy" @click="dlgRequest = false">Cancel</b-button>
        <b-button type="submit" variant="primary" class="ml-2" :disabled="busy" @click="onSubmitRequest">
          <i class="fa fa-refresh fa-spin" v-if="busy"></i> Submit
        </b-button>
      </div>
    </b-modal>

    <!-- Request Confirmation Modal Dialog -->
    <b-modal id="dlgRequestConfirm" class="modal-primary" hide-header-close v-model="dlgRequestConfirm">
      <div class="font-weight-bold" slot="modal-title">
        <i class="icon-paper-plane pr-2"></i>Threat or Vulnerability Request
      </div>
      <p>Request submitted. We will be in contact soon with an update. Thank you for your input!</p>
      <div slot="modal-footer">
        <b-button variant="primary" @click="dlgRequestConfirm = false">OK</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { authService, riskService } from '@/services'
import Enumerable from 'linq/linq.js'

export default {
  name: 'RiskAnalysisThreats',
  data() {
    return {
      rm: {
        threats: []
      },
      currentThreats: [],
      dlgRequest: false,
      dlgRequestMsg: '',
      dlgRequestSubmitted: false,
      dlgRequestConfirm: false,
      ready: false,
      busy: false,
      submitted: false,
      currentStep: 2
    }
  },
  computed: {
    ...mapState(['rmSteps']),
    ...mapState('maps', ['threatMap']),
    ...mapState('user', ['user']),
    activeThreats() {
      return Enumerable.from(this.rm.threats).where(x => { return x.active }).count();
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertException: 'alert/exception'
    }),
    getThreats(g) {
      return Enumerable.from(g)
        .where(x => { return x.current; })
        .groupBy(x => { return x.type; }, null, (key, g) => { return { type: key, threats: this.computeThreats(g) }; })
        .toArray();
    },
    computeThreats(g) {
      return Enumerable.from(g)
        .groupBy(x => { return x.threat; }, null, (key, g) => { return { threat: key, vulns: this.computeVulns(g), count: this.computeVulnsCount(g) }; })
        .toArray();
    },
    computeVulns(g) {
      return g.toArray();
    },
    computeVulnsCount(g) {
      return g.where(x => { return x.active; }).count();
    },
    toggle(type, threat, vuln) {
      // find the threat
      let item = Enumerable.from(this.rm.threats)
        .where(x => { return (x.id == vuln.id); })
        .firstOrDefault();

      // insert a default if it doesn't exist
      if (!item) {
        if (!this.rm.threats) {
          this.rm.threats = [];
        }
        item = { id: vuln.id, revision: vuln.revision, active: false };
        this.rm.threats.push(item);
      }

      // update revision - this is handled by the services (it updates when you get the latest RM) so don't do it here!
      // item.revision = vuln.revision;

      // toggle threat as active/inactive
      let wasActive = item.active ? true : false;
      this.$set(item, 'active', !wasActive); // must use Vue.set to get things to be reactive to the change
      this.$set(vuln, 'active', !wasActive); // must use Vue.set to get things to be reactive to the change

      // update threat count
      if (wasActive) {
        this.$set(threat, 'count', threat.count - 1);
      } else {
        this.$set(threat, 'count', threat.count + 1);
      }
    },
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.rm.step = this.currentStep;
      riskService.setRiskManagement(this.rm)
        .then(data => {
          this.$router.push('/risk/level');
        })
        .catch(err => {
          this.alertException({ err, message: 'Error saving risk management.' });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onSubmitRequest() {
      this.alertClear();
      this.busy = true;
      this.dlgRequestSubmitted = true;

      this.$validator.validate()
        .then(valid => {
          if (valid) {
            return authService.submitFeedback(this.dlgRequestMsg, this.user, this.$route)
              .then(data => {
                this.dlgRequest = false;
                this.dlgRequestConfirm = true;
              }).catch(err => {
                this.alertException({ err, message: 'Failed to submit request, please try again later.' });
              }).finally(() => {
                this.busy = false;
              });
          } else {
            this.busy = false;
          }
        });
    }
  },
  created() {
    this.ready = false;

    Promise.all([
      this.$store.dispatch('maps/fetchThreatMap'),
      riskService.getCurrentRiskManagement()
    ]).then((values) => {
      this.rm = values[1];

      // build current threat map and groupings, add count and active to current threats to help optimize UI tracking and efficiency
      let map = Enumerable.from(this.threatMap)
        .groupJoin(this.rm.threats,
          o => { return o.id + o.revision; },
          i => { return i.id + i.revision; },
          (o, i) => {
            // copy active flag from rm threats to current threat list
            let x = i.toArray();
            return { ...o, active: x.length > 0 ? x[0].active : false }
          })
        .toArray();
      this.currentThreats = this.getThreats(map);

    }).catch((err) => {
      this.alertException({ err, message: 'Error loading risk management data.' });
    }).finally(() => {
      this.ready = true;
    })
  }
}
</script>

<style>
.collapsed > .when-opened,
:not(.collapsed) > .when-closed {
  display: none;
}
</style>